// @tennis-matches/services/hub
// Hub-Onboarding-State: leitet aus bestehenden RBAC-Bindings + Access-Requests ab,
// welche der drei Aktivitäten ("Verein verbinden", "Familie", "Training")
// erledigt, ausstehend oder noch offen sind. Persistiert "später"-Dismissals
// im bestehenden user_settings-Bucket – ohne neue Tabellen, ohne neue RPCs.

import { loadUserSettings, saveUserSettings } from "../settings/index.js";
import { hasActiveTenantRoleBindings } from "../governance/effective-visible-apps.js";
import {
  HUB_ACTIVITIES,
  getActivityCompletionRoles,
} from "../governance/activity-role-mapping.js";

/** @typedef {"open"|"pending"|"completed"|"dismissed"} HubActivityStatus */

const USER_SETTINGS_DISMISSED_KEY = "hubOnboardingDismissedActivities";
const USER_SETTINGS_DISMISSED_KEY_SNAKE = "hub_onboarding_dismissed_activities";

const STAFF_ROLE_SET = new Set([
  "trainer",
  "trainer_coordinator",
  "vereinsadmin",
  "vorstand",
  "jugendwart",
  "sportwart",
  "kassenwart",
  "event_organizer",
  "team_captain",
]);

function __asLower(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function __activeBindings(bindings) {
  return (Array.isArray(bindings) ? bindings : []).filter(
    (b) => b && (!("is_active" in b) || b.is_active !== false),
  );
}

function __bindingsIncludeRole(bindings, roleId) {
  const target = __asLower(roleId);
  if (!target) return false;
  return __activeBindings(bindings).some((b) => __asLower(b?.role) === target);
}

function __bindingsIncludeAnyRole(bindings, roleIds) {
  return (roleIds || []).some((role) => __bindingsIncludeRole(bindings, role));
}

function __pendingRequestIncludesAnyRole(accessRequests, roleIds) {
  const wanted = new Set((roleIds || []).map(__asLower));
  if (!wanted.size) return false;
  const list = Array.isArray(accessRequests) ? accessRequests : [];
  return list.some((req) => {
    if (!req || __asLower(req.status) !== "pending") return false;
    const requested = Array.isArray(req.requested_roles)
      ? req.requested_roles
      : [];
    return requested.some((role) => wanted.has(__asLower(role)));
  });
}

/**
 * Liefert für jede Aktivität den abgeleiteten Status (open/pending/completed/dismissed).
 *
 * @param {Object} input
 * @param {Array}  input.bindings           - active role_bindings aus Governance-Bundle
 * @param {Array}  [input.accessRequests]   - aktuelle access_requests des Nutzers
 * @param {boolean}[input.hasPendingNewClub]- pending registration_onboarding_request vorhanden?
 * @param {string[]} [input.dismissedActivityIds] - aus user_settings
 * @param {boolean} [input.viewerIsManagedDependent] - Kind/Familienmitglied mit fremdem Rechnungsempfänger
 * @returns {Record<string, HubActivityStatus>}
 */
export function deriveHubActivityStatuses({
  bindings = [],
  accessRequests = [],
  hasPendingNewClub = false,
  dismissedActivityIds = [],
  viewerIsManagedDependent = false,
} = {}) {
  const active = __activeBindings(bindings);
  const dismissedSet = new Set((dismissedActivityIds || []).map(__asLower));
  const hasAnyTenantBinding = hasActiveTenantRoleBindings(active);
  const hasAnyTenantPending = (accessRequests || []).some(
    (req) =>
      __asLower(req?.status) === "pending" &&
      __asLower(req?.scope) === "tenant",
  );
  /** @type {Record<string, HubActivityStatus>} */
  const result = {};

  for (const activity of HUB_ACTIVITIES) {
    if (dismissedSet.has(activity.id)) {
      result[activity.id] = "dismissed";
      continue;
    }

    if (activity.id === "connect_club") {
      if (hasAnyTenantBinding) {
        result[activity.id] = "completed";
      } else if (hasAnyTenantPending || hasPendingNewClub) {
        result[activity.id] = "pending";
      } else {
        result[activity.id] = "open";
      }
      continue;
    }

    if (activity.id === "family" && viewerIsManagedDependent) {
      result[activity.id] = "completed";
      continue;
    }

    // Familie anlegen ist Eltern-Aufgabe – reine Spieler sehen den Schritt nicht.
    if (
      activity.id === "family" &&
      __bindingsIncludeRole(active, "player") &&
      !__bindingsIncludeRole(active, "parent")
    ) {
      result[activity.id] = "completed";
      continue;
    }

    const completionRoles = getActivityCompletionRoles(activity.id);
    let completed = __bindingsIncludeAnyRole(active, completionRoles);
    // Trainer/Staff geben Training – „Training entdecken“ (Spieler-Onboarding) entfällt.
    if (activity.id === "training" && hasAnyStaffRoleBinding(active)) {
      completed = true;
    }
    if (completed) {
      result[activity.id] = "completed";
      continue;
    }

    const pendingForActivity = __pendingRequestIncludesAnyRole(
      accessRequests,
      completionRoles,
    );
    result[activity.id] = pendingForActivity ? "pending" : "open";
  }

  return result;
}

/** Liest die persistierten Dismissals aus user_settings (nie throw). */
export async function loadDismissedHubActivities() {
  try {
    const res = await loadUserSettings();
    const settings =
      res && typeof res.settings === "object" ? res.settings : {};
    const raw =
      settings[USER_SETTINGS_DISMISSED_KEY] ??
      settings[USER_SETTINGS_DISMISSED_KEY_SNAKE] ??
      [];
    if (!Array.isArray(raw)) return [];
    return raw
      .map((id) => __asLower(id))
      .filter((id) => HUB_ACTIVITIES.some((a) => a.id === id));
  } catch {
    return [];
  }
}

async function __persistDismissedList(nextList) {
  const current = await loadUserSettings();
  const existing =
    current?.settings && typeof current.settings === "object"
      ? current.settings
      : {};
  const merged = {
    ...existing,
    [USER_SETTINGS_DISMISSED_KEY]: nextList,
    [USER_SETTINGS_DISMISSED_KEY_SNAKE]: nextList,
  };
  return saveUserSettings(merged);
}

/** Persistiert das Dismissal einer Aktivität – additiv, idempotent. */
export async function dismissHubActivityRemote(activityId) {
  const id = __asLower(activityId);
  if (!id || !HUB_ACTIVITIES.some((a) => a.id === id)) {
    return { ok: false, reason: "unknown-activity" };
  }
  const current = await loadDismissedHubActivities();
  if (current.includes(id)) return { ok: true, dismissed: current };
  const next = [...current, id];
  const res = await __persistDismissedList(next);
  if (!res?.ok) return { ok: false, reason: "save-failed" };
  return { ok: true, dismissed: next };
}

/** Hebt das Dismissal einer Aktivität auf (für erneutes Zeigen im Hero). */
export async function undismissHubActivityRemote(activityId) {
  const id = __asLower(activityId);
  if (!id) return { ok: false, reason: "unknown-activity" };
  const current = await loadDismissedHubActivities();
  if (!current.includes(id)) return { ok: true, dismissed: current };
  const next = current.filter((x) => x !== id);
  const res = await __persistDismissedList(next);
  if (!res?.ok) return { ok: false, reason: "save-failed" };
  return { ok: true, dismissed: next };
}

/**
 * Setzt alle „Später"-Markierungen zurück, sodass der Hero wieder vollständig
 * angezeigt wird. Nutzer-getriggert (Profil-Menü → „Onboarding neu starten").
 */
export async function undismissAllHubActivitiesRemote() {
  const current = await loadDismissedHubActivities();
  if (!current.length) return { ok: true, dismissed: [] };
  const res = await __persistDismissedList([]);
  if (!res?.ok) return { ok: false, reason: "save-failed" };
  return { ok: true, dismissed: [] };
}

/** Profil-Komplettheit für kollabierten Hero-Modus. */
export function isHubOnboardingProfileComplete(statuses) {
  if (!statuses || typeof statuses !== "object") return false;
  return HUB_ACTIVITIES.every((a) => {
    const s = statuses[a.id];
    return s === "completed" || s === "dismissed";
  });
}

/**
 * Zeigt den Onboarding-Hero nur bei offenen/laufenden Aufgaben oder ausstehender Datenfreigabe.
 * Erledigte und per „Brauche ich nicht“ ausgeblendete Aktivitäten blenden den Hero aus.
 */
export function shouldShowHubOnboardingHero({
  statuses = {},
  dataConsentVisible = false,
} = {}) {
  if (dataConsentVisible) return true;
  return HUB_ACTIVITIES.some((a) => {
    const s = statuses[a.id];
    return s === "open" || s === "pending";
  });
}

/** Hilfsfunktion: Welche Aktivitäten würden im Hero noch sichtbar sein? */
export function getActivitiesVisibleInHero(statuses) {
  if (!statuses || typeof statuses !== "object") return [];
  return HUB_ACTIVITIES.filter((a) => {
    const s = statuses[a.id];
    return s === "open" || s === "pending" || s === "completed";
  });
}

/** Diagnose-Helfer: Hat der Nutzer ausschließlich Family-Rollen? */
export function hasOnlyFamilyRoleBindings(bindings) {
  const active = __activeBindings(bindings);
  if (!active.length) return false;
  return active.every((b) => {
    const role = __asLower(b?.role);
    return role === "parent" || role === "player";
  });
}

/** Diagnose-Helfer: Hat der Nutzer mindestens eine Staff-Rolle? */
export function hasAnyStaffRoleBinding(bindings) {
  const active = __activeBindings(bindings);
  return active.some((b) => STAFF_ROLE_SET.has(__asLower(b?.role)));
}

/**
 * Liefert die einzelnen, eindeutigen Tenant-Scopes des Nutzers mit Label.
 * Wird im Hero zur Bestätigung der vom Trainer vorbereiteten Vereinszuordnung
 * und im Profil-Dropdown ("Meine Vereine") verwendet.
 *
 * @param {Array} bindings
 * @returns {{ id: string, label: string, roles: string[] }[]}
 */
export function deriveMyClubsFromBindings(bindings) {
  const map = new Map();
  for (const b of __activeBindings(bindings)) {
    if (__asLower(b?.scope) !== "tenant") continue;
    const id = String(b?.scope_id || "").trim();
    if (!id) continue;
    const label = String(
      b?.scope_label ||
        b?.tenant_label ||
        b?.tenant_name ||
        b?.scope_id ||
        "Verein",
    ).trim();
    if (!map.has(id)) {
      map.set(id, { id, label, roles: [] });
    }
    const role = __asLower(b?.role);
    const entry = map.get(id);
    if (role && !entry.roles.includes(role)) entry.roles.push(role);
  }
  return Array.from(map.values());
}

/**
 * Hängt RPC-aufgelöste Tenant-Labels an Club-Einträge (Avatar-Menü „Meine Vereine“).
 *
 * @param {{ id: string, label: string, roles?: string[] }[]} clubs
 * @param {Record<string, { display_label?: string, display_name?: string }>} byScopeId
 * @param {(key: string, params?: object) => string} [t]
 */
export function enrichMyClubsWithTenantLabels(clubs, byScopeId, t) {
  const tr = typeof t === "function" ? t : (key) => key;
  return (Array.isArray(clubs) ? clubs : []).map((club) => {
    const id = String(club?.id || "").trim();
    if (!id) return club;
    const entry = byScopeId?.[id] || byScopeId?.[id.toLowerCase()] || null;
    const resolved = String(
      entry?.display_label || entry?.display_name || "",
    ).trim();
    if (resolved) return { ...club, label: resolved };
    const currentLabel = String(club?.label || "").trim();
    if (currentLabel && currentLabel !== id) return club;
    return {
      ...club,
      label: tr("hub.myClubs.fallbackName", { id }),
    };
  });
}

function __normalizeScopeId(id) {
  return String(id ?? "")
    .trim()
    .toLowerCase();
}

/**
 * Hat der Nutzer Tenant-Bindings, ohne dass er jemals selbst einen
 * `access_request` für diesen Tenant gestellt hat? Dann ist die Bindung
 * höchstwahrscheinlich durch einen Trainer-/Admin-Invite entstanden und
 * wir bitten den Nutzer um eine kurze Bestätigung ("Stimmt das?").
 *
 * Reine Heuristik: keine DB-Änderung, nur Ableitung aus bestehenden
 * `role_bindings` + `access_requests`.
 *
 * @param {{ bindings?: Array, accessRequests?: Array, contactInviteTenantIds?: string[] }} input
 * @returns {{ needsConfirmation: boolean, clubs: { id: string, label: string, roles: string[] }[] }}
 */
export function deriveInvitedClubsAwaitingConfirmation({
  bindings = [],
  accessRequests = [],
  contactInviteTenantIds = [],
} = {}) {
  const clubs = deriveMyClubsFromBindings(bindings);
  if (!clubs.length) return { needsConfirmation: false, clubs: [] };

  const selfRequestedScopeIds = new Set(
    (Array.isArray(accessRequests) ? accessRequests : [])
      .filter((req) => {
        const status = __asLower(req?.status);
        return (
          status === "approved" || status === "pending" || status === "granted"
        );
      })
      .map((req) => __normalizeScopeId(req?.scope_id))
      .filter(Boolean),
  );

  for (const tid of contactInviteTenantIds) {
    const id = __normalizeScopeId(tid);
    if (id) selfRequestedScopeIds.add(id);
  }

  const invited = clubs.filter(
    (c) => !selfRequestedScopeIds.has(__normalizeScopeId(c.id)),
  );
  return { needsConfirmation: invited.length > 0, clubs: invited };
}

const USER_SETTINGS_CONFIRMED_INVITE_KEY =
  "hubOnboardingConfirmedInviteScopeIds";
const USER_SETTINGS_CONFIRMED_INVITE_KEY_SNAKE =
  "hub_onboarding_confirmed_invite_scope_ids";

/** Liest die vom Nutzer bestätigten Invite-Tenant-Scopes aus user_settings. */
export async function loadConfirmedInviteScopeIds() {
  try {
    const res = await loadUserSettings();
    const settings =
      res && typeof res.settings === "object" ? res.settings : {};
    const raw =
      settings[USER_SETTINGS_CONFIRMED_INVITE_KEY] ??
      settings[USER_SETTINGS_CONFIRMED_INVITE_KEY_SNAKE] ??
      [];
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => String(id || "").trim()).filter(Boolean);
  } catch {
    return [];
  }
}

/** Merkt sich, dass der Nutzer einen Invite-Tenant bestätigt hat. */
export async function confirmInvitedClubRemote(scopeId) {
  const id = String(scopeId || "").trim();
  if (!id) return { ok: false, reason: "invalid-scope" };
  const current = await loadConfirmedInviteScopeIds();
  if (current.includes(id))
    return { ok: true, confirmed: current, persisted: true };
  const next = [...current, id];
  const settings = await loadUserSettings();
  const existing =
    settings?.settings && typeof settings.settings === "object"
      ? settings.settings
      : {};
  const merged = {
    ...existing,
    [USER_SETTINGS_CONFIRMED_INVITE_KEY]: next,
    [USER_SETTINGS_CONFIRMED_INVITE_KEY_SNAKE]: next,
  };
  const res = await saveUserSettings(merged);
  if (!res?.ok) return { ok: false, reason: "save-failed" };
  // saveUserSettings liefert ok:true sowohl bei DB-Persistenz als auch bei
  // reinem localStorage-Fallback. Für die Vereinsbestätigung MÜSSEN wir
  // wissen, ob es DB-persistiert wurde – sonst kommt der Banner nach Reload
  // auf einem anderen Gerät / nach localStorage-Reset zurück.
  if (res.persisted === false) {
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(
        "[hub-onboarding] confirmInvitedClubRemote: DB-Persistenz fehlgeschlagen, nur localStorage:",
        res.reason || "(no reason)",
      );
    }
    return {
      ok: false,
      reason: res.reason || "not-persisted",
      confirmed: next,
      persisted: false,
    };
  }
  return { ok: true, confirmed: next, persisted: true };
}
